import React, { useMemo, ReactNode, FC } from "react";
import { TaxLabel } from "~/gql/graphql";
import {
  OnPriceChange,
  MatterType,
  MatterTemplate,
  MatterFormMode,
  OnPriceRangeChange,
  PricingOptions,
} from "../../data-types";

import { MatterEditorViewProps, MatterEditorView } from "../matter-editor";

const NULL_CALLBACK = () => {};
const ASYNC_CALLBACK = async () => {};

export type MatterTemplateEditorViewProps<T extends MatterType, U extends MatterFormMode> = Pick<
  MatterEditorViewProps<T, U>,
  "tabs" | "activeTab" | "onChangeTab" | "settings"
> & {
  matterTemplate: MatterTemplate;
  onTitleChange: (newTitle: string) => void;
  pricingSummaries: PricingOptions;
  onChosenPriceChange: OnPriceChange;
  onChosenRangePriceChange: OnPriceRangeChange;
  versionsSidebar: ReactNode;
  pricingSummaryFooter: ReactNode;
  onPhasePriceChange: (phaseId: number, priceDetails: Partial<PricingOptions>) => void;
  taxLevelPercent: number;
  excludeTax: boolean;
  onFixedPriceAlternativeChange?: OnPriceChange;
  fixedPriceAlternative?: number | null;
  currencySymbol: string;
  taxLabel: TaxLabel;
};

export const MatterTemplateEditorView = <T extends MatterType, U extends MatterFormMode>({
  matterTemplate,
  tabs,
  activeTab,
  onChangeTab,
  settings,
}: MatterTemplateEditorViewProps<T, U>): ReturnType<FC<MatterTemplateEditorViewProps<T, U>>> => {
  const matter = useMemo<MatterTemplate>(
    () => ({
      template: true,
      ...matterTemplate,
      client: null,
      externalReference: null,
      uplifts: [],
      discounts: [],
    }),
    [matterTemplate]
  );

  return (
    <MatterEditorView
      matter={matter as MatterTemplate}
      onClientSave={NULL_CALLBACK}
      onMatterStatusUpdate={ASYNC_CALLBACK}
      onMatterExternalMatterUpdate={ASYNC_CALLBACK}
      onMatterUpdate={ASYNC_CALLBACK}
      tabs={tabs}
      activeTab={activeTab}
      onChangeTab={onChangeTab}
      settings={settings}
      editorUnlocked={true}
      isTemplate={true}
      sidebarContents={{ _: <></> }}
    />
  );
};
